import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { OrganizationCreate, OrganizationUpdate } from '@/lib/schemas';
import { toast } from 'sonner';
import { invalidateProjectListQueries } from '@/lib/cache-utils';

// ============================================================================
// Query Hooks
// ============================================================================

export function useOrganizations(
  // Phase 7.5.20: Added category_id to type definition (was missing but being passed)
  // Phase 9.13a: Added has_category and has_role for filtering
  params?: { role?: string; search?: string; limit?: number; offset?: number; category_id?: string; has_category?: boolean; has_role?: boolean },
  options?: { keepPreviousData?: boolean; enabled?: boolean }
) {
  return useQuery({
    queryKey: ['organizations', params],
    queryFn: () => api.organizations.list(params),
    placeholderData: options?.keepPreviousData ? (prev) => prev : undefined,
    enabled: options?.enabled ?? true,
    staleTime: 60_000, // 1 min — org list doesn't change often
  });
}

export function useOrganization(id: string | undefined) {
  return useQuery({
    queryKey: ['organizations', id],
    queryFn: () => api.organizations.get(id!),
    enabled: !!id,
  });
}

// Phase 7 Batch 1: Search hook with debouncing
export function useOrganizationSearch(
  query: string,
  role?: string,
  categoryId?: string,
  options?: { enabled?: boolean; debounce?: number }
) {
  return useQuery({
    queryKey: ['organizations', 'search', { q: query, role, category_id: categoryId }],
    queryFn: () => api.organizations.search({ q: query, role, category_id: categoryId }),
    enabled: (options?.enabled ?? true) && query.length > 0, // Only search if query exists
    staleTime: 30000, // Cache for 30 seconds
  });
}

// Wave 3 v0.14 org-change §5.0: cross-entity picker search for the
// OrgChangeStepper Step 1 picker. Returns both organizations and DBAs
// (organization_establishments) in a single response, ordered orgs-first
// then alphabetical, archived DBAs filtered by default.
//
// Debounce is owned by the consumer (the stepper uses a 250ms debounce on
// the search input). This hook fires whenever `query` changes — pair it
// with a debounced state value upstream.
//
// IMPORTANT: pass-through of `data` shape (results: [...]) — do NOT
// destructure to individual fields (feedback_mutation_hook_field_drift).
export function useOrgChangeSearch(
  query: string,
  options?: { enabled?: boolean; includeArchived?: boolean; limit?: number }
) {
  const enabled = (options?.enabled ?? true) && query.length > 0;
  return useQuery({
    queryKey: [
      'organizations',
      'org-change-search',
      { q: query, include_archived: options?.includeArchived ?? false, limit: options?.limit ?? 50 },
    ],
    queryFn: () =>
      api.organizations.orgChangeSearch({
        q: query,
        include_archived: options?.includeArchived ?? false,
        limit: options?.limit ?? 50,
      }),
    enabled,
    staleTime: 30_000,
  });
}

// Phase 7 Batch 1: Recent organizations hook
export function useRecentOrganizations(
  role?: string,
  categoryId?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['organizations', 'recent', { role, category_id: categoryId }],
    queryFn: () => api.organizations.getRecent({ role, category_id: categoryId }),
    enabled: options?.enabled ?? true,
    staleTime: 60000, // Cache for 1 minute
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useCreateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: OrganizationCreate) => api.organizations.create(data),
    onSuccess: (response) => {
      // Invalidate without await - component navigates to list page which will refetch
      // Follows simplified pattern (like billable items, clients, permits)
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      // Also invalidate clients cache since clients are filtered organizations (is_client=true)
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Organization created successfully');
      return response.organization;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create organization');
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OrganizationUpdate }) =>
      api.organizations.update(id, data),
    onSuccess: (response, variables) => {
      // Invalidate without await - component navigates to list page which will refetch
      // Follows simplified pattern (like billable items, clients, permits)
      queryClient.invalidateQueries({ queryKey: ['organizations'] }); // Prefix match: invalidates all organization queries
      // Also invalidate clients cache since clients are filtered organizations (is_client=true)
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      // Phase 9.25d: Invalidate invoices - org data (legal_name, DBA, address) is JOINed into invoice queries
      // Without this, invoice pages show stale org data after org updates
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      // Also invalidate project list queries since they display org info
      // Exclude detail queries to prevent stale reads overwriting fresh cache
      invalidateProjectListQueries(queryClient);
      toast.success('Organization updated successfully');
      return response.organization;
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update organization');
    },
  });
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.organizations.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['organizations'] });

      // Snapshot previous value
      const previousOrganizations = queryClient.getQueriesData({ queryKey: ['organizations'] });

      // Optimistically update all organization queries
      queryClient.setQueriesData<any>(
        { queryKey: ['organizations'] },
        (old) => {
          if (!old) return old;
          // Handle both list response (has organizations array) and detail response (single org)
          if (Array.isArray(old.organizations)) {
            return {
              ...old,
              organizations: old.organizations.filter((org: any) => org.id !== id),
              total: old.total - 1, // Update total count for pagination
            };
          }
          // If not a list query, just return old (will be invalidated anyway)
          return old;
        }
      );

      return { previousOrganizations };
    },
    onError: (error: any, id, context) => {
      // Rollback on error
      if (context?.previousOrganizations) {
        context.previousOrganizations.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast.error(error.message || 'Failed to delete organization');
    },
    onSuccess: () => {
      // Invalidate clients cache since clients are filtered organizations (is_client=true)
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      // Phase 9.25d: Invalidate invoices/projects - org deletion affects related records
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      invalidateProjectListQueries(queryClient);
      toast.success('Organization deleted successfully');
    },
  });
}
